<!-- src/apps/workspace/components/domains/DnsWidget.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import Skeleton from '@/shared/components/closet/Skeleton.vue';
  import { useDnsWidget, type DnsRecord } from '@/shared/composables/useDnsWidget';
  import { computed, onMounted, watch } from 'vue';

  interface Props {
    /** The domain to configure DNS for (e.g., "uk.metalbaum.com") */
    domain: string;
    /** The IP address or hostname to point the domain at */
    targetAddress: string;
    /** Whether this is an apex domain (requires A record instead of CNAME) */
    isApex?: boolean;
    /**
     * TXT record host for domain ownership validation.
     * From backend: includes TRD suffix (e.g., "_onetime-challenge-019bdd2.uk")
     * @see lib/onetime/models/custom_domain.rb#generate_txt_validation_record
     */
    txtValidationHost?: string;
    txtValidationValue?: string;
    /**
     * Transit routing domain (subdomain portion), e.g., "uk" for "uk.metalbaum.com".
     * Used to normalize txtValidationHost for the Approximated widget.
     */
    trd?: string;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<{
    (e: 'records-verified', records: unknown[]): void;
    (e: 'verification-failed', records: unknown[]): void;
    (e: 'partial-verification', records: unknown[]): void;
  }>();

  const { t } = useI18n();

  /**
   * Normalize the TXT validation host for the Approximated DNS widget.
   *
   * BACKGROUND: The Ruby backend and the Approximated widget handle DNS record
   * construction differently:
   *
   * Ruby (lib/onetime/models/custom_domain.rb#validate_ownership via ApproximatedStrategy):
   *   - Sends full FQDN as `address`: "_onetime-challenge-019bdd2.uk.metalbaum.com"
   *   - Approximated API does a direct DNS lookup for this exact address
   *
   * Approximated Widget (dnswidget.v1.js):
   *   - Receives separate `host` and `domain` fields
   *   - The widget API combines these, extracting the subdomain from `domain`
   *   - If we pass host="_onetime-challenge-019bdd2.uk" with domain="uk.metalbaum.com",
   *     the API produces "uk" + "_onetime-challenge-019bdd2.uk" = double "uk"
   *
   * FIX: Strip the TRD suffix from txtValidationHost before passing to the widget,
   * so the widget can correctly reconstruct the full record name.
   *
   * Example for "uk.metalbaum.com":
   *   - Backend txtValidationHost: "_onetime-challenge-019bdd2.uk"
   *   - TRD: "uk"
   *   - Widget host (after normalization): "_onetime-challenge-019bdd2"
   *   - Widget reconstructs: "_onetime-challenge-019bdd2.uk.metalbaum.com" ✓
   */
  const normalizedTxtValidationHost = computed(() => {
    if (!props.txtValidationHost) return undefined;
    if (!props.trd) return props.txtValidationHost;

    // Strip trailing ".{trd}" suffix if present
    const suffix = `.${props.trd}`;
    if (props.txtValidationHost.endsWith(suffix)) {
      return props.txtValidationHost.slice(0, -suffix.length);
    }
    return props.txtValidationHost;
  });

  // Build DNS records based on whether it's an apex domain
  const dnsRecords = computed<DnsRecord[]>(() => {
    const records: DnsRecord[] = [];

    // For apex domains, use A record; otherwise use CNAME
    if (props.isApex) {
      records.push({
        type: 'A',
        host: '@',
        value: props.targetAddress,
        ttl: 3600,
      });
    } else {
      records.push({
        type: 'CNAME',
        host: '@',
        value: props.targetAddress,
        ttl: 3600,
      });
    }

    // Add TXT record for validation if provided
    // Use normalized host to avoid double-subdomain issue with widget
    if (normalizedTxtValidationHost.value && props.txtValidationValue) {
      records.push({
        type: 'TXT',
        host: normalizedTxtValidationHost.value,
        value: props.txtValidationValue,
        ttl: 3600,
      });
    }

    return records;
  });

  const { isLoading, error, initWidget, stopWidget } = useDnsWidget({
    dnsRecords: dnsRecords,
    domain: props.domain,
    onRecordsVerified: (records) => emit('records-verified', records),
    onVerificationFailed: (records) => emit('verification-failed', records),
    onPartialVerification: (records) => emit('partial-verification', records),
  });

  // Initialize widget when component mounts
  onMounted(async () => {
    await initWidget();
  });

  // Re-initialize if domain changes
  watch(
    () => props.domain,
    async (newDomain, oldDomain) => {
      if (newDomain !== oldDomain && newDomain) {
        stopWidget();
        await initWidget();
      }
    }
  );
</script>

<template>
  <div class="dns-widget-container">
    <!-- Loading state -->
    <div
      v-if="isLoading"
      role="status"
      aria-busy="true"
      class="animate-pulse space-y-3 py-8 motion-reduce:animate-none">
      <span class="sr-only">{{ t('web.COMMON.loading') }}</span>
      <Skeleton
        :count="4"
        height="h-12"
        rounded="rounded-md"
        :pulse="false" />
    </div>

    <!-- Error state -->
    <div
      v-else-if="error"
      class="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
      <p class="text-red-700 dark:text-red-400">
        {{ error }}
      </p>
    </div>

    <!-- Widget container -->
    <div
      v-show="!isLoading && !error"
      id="apxdnswidget"
      class="apxdnswidget" ></div>
  </div>
</template>

<style>
  /* Dark mode overrides for Approximated DNS widget */
  .dark .apxdnswidget {
    --text-color: #e5e7eb;
    --light-text-color: #9ca3af;
    --link-text-color: #60a5fa;
    --main-bg-color: transparent;
    --shaded-bg-color: #1f2937;
    --shaded-border: 1px solid #374151;
    --button-bg-color: #3b82f6;
    --button-text-color: #ffffff;
    --border-color: #374151;
  }

  /* Ensure all widget inputs match dark mode */
  .dark .apxdnswidget input,
  .dark .apxdnswidget textarea {
    background-color: #374151;
    border-color: #4b5563;
    color: #f3f4f6;
  }

  .dark .apxdnswidget input:focus,
  .dark .apxdnswidget textarea:focus {
    border-color: #3b82f6;
  }

  /* Read-only copy fields (the instruction values) */
  .dark .apxdnswidget .apxdns-input-copy-container input {
    background-color: #1f2937;
    border-color: #374151;
    color: #f3f4f6;
  }

  /* Copy button styling */
  .dark .apxdnswidget .apxdns-copy-button {
    background-color: #3b82f6;
    color: #ffffff;
  }

  .dark .apxdnswidget .apxdns-button {
    background-color: #3b82f6;
    border-color: #3b82f6;
  }

  .dark .apxdnswidget .apxdns-button:hover {
    background-color: #2563eb;
    border-color: #2563eb;
  }

  /* Record display values */
  .dark .apxdnswidget .apxdns-domainconnect-record-display-host-value,
  .dark .apxdnswidget .apxdns-domainconnect-record-display-value-value,
  .dark .apxdnswidget .apxdns-domainconnect-record-display-ttl-value {
    background-color: #1f2937;
    color: #f3f4f6;
  }

  /* Widget container styling */
  .dns-widget-container {
    margin-top: 1.5rem;
  }

  .dns-widget-container .apxdnswidget {
    max-width: 100%;
  }
</style>
